import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { 
  GripVertical, 
  Clock, 
  Utensils, 
  Star, 
  Camera, 
  Music, 
  X,
  Save,
  RotateCcw
} from 'lucide-react';

const DragDropActivity = ({ 
  day, 
  dayIndex, 
  onSave, 
  onCancel, 
  isOpen, 
  onClose 
}) => {
  // Handle both new timedActivities structure and legacy timeBlocks structure
  const getInitialTimeBlocks = () => {
    if (day.timedActivities) {
      return {
        morning: day.timedActivities.morning || [],
        afternoon: day.timedActivities.afternoon || [],
        evening: day.timedActivities.evening || []
      };
    } else if (day.timeBlocks) {
      return {
        morning: day.timeBlocks.morning?.activities || [],
        afternoon: day.timeBlocks.afternoon?.activities || [],
        evening: day.timeBlocks.evening?.activities || []
      };
    }
    return { morning: [], afternoon: [], evening: [] };
  };

  const [localTimeBlocks, setLocalTimeBlocks] = useState(getInitialTimeBlocks());
  
  const [localDining, setLocalDining] = useState({
    lunch: day.dining?.lunch || null,
    dinner: day.dining?.dinner || null
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [removingActivity, setRemovingActivity] = useState(null);
  const [editingTime, setEditingTime] = useState(null);

  const timeBlocks = [
    { id: 'morning', title: 'Morning', icon: Clock, color: 'yellow' },
    { id: 'afternoon', title: 'Afternoon', icon: Utensils, color: 'orange' },
    { id: 'evening', title: 'Evening', icon: Star, color: 'purple' }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'ride': return Star;
      case 'show': return Camera;
      case 'transportation': return Utensils;
      default: return Clock;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'ride': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'show': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'transportation': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const sourceTimeBlock = source.droppableId;
    const destTimeBlock = destination.droppableId;

    if (sourceTimeBlock === destTimeBlock) {
      // Reordering within same time block
      const newItems = Array.from(localTimeBlocks[sourceTimeBlock]);
      const [reorderedItem] = newItems.splice(source.index, 1);
      newItems.splice(destination.index, 0, reorderedItem);

      setLocalTimeBlocks(prev => ({
        ...prev,
        [sourceTimeBlock]: newItems
      }));
    } else {
      // Moving between time blocks
      const sourceItems = Array.from(localTimeBlocks[sourceTimeBlock]);
      const destItems = Array.from(localTimeBlocks[destTimeBlock]);
      const [movedItem] = sourceItems.splice(source.index, 1);
      destItems.splice(destination.index, 0, movedItem);

      setLocalTimeBlocks(prev => ({
        ...prev,
        [sourceTimeBlock]: sourceItems,
        [destTimeBlock]: destItems
      }));
    }

    setHasChanges(true);
  };

  const handleSave = () => {
    const updatedDay = {
      ...day,
      // Support both new timedActivities structure and legacy timeBlocks structure
      ...(day.timedActivities ? {
        timedActivities: {
          morning: localTimeBlocks.morning,
          afternoon: localTimeBlocks.afternoon,
          evening: localTimeBlocks.evening
        }
      } : {
        timeBlocks: {
          morning: {
            ...day.timeBlocks?.morning,
            activities: localTimeBlocks.morning
          },
          afternoon: {
            ...day.timeBlocks?.afternoon,
            activities: localTimeBlocks.afternoon
          },
          evening: {
            ...day.timeBlocks?.evening,
            activities: localTimeBlocks.evening
          }
        }
      }),
      dining: localDining
    };
    
    onSave(dayIndex, updatedDay);
    setHasChanges(false);
  };

  const handleReset = () => {
    setLocalTimeBlocks(getInitialTimeBlocks());
    setLocalDining({
      lunch: day.dining?.lunch || null,
      dinner: day.dining?.dinner || null
    });
    setHasChanges(false);
  };

  const handleRemoveActivity = (timeBlockId, activityIndex) => {
    const activity = localTimeBlocks[timeBlockId][activityIndex];
    setRemovingActivity({ timeBlockId, activityIndex, activity });
  };

  const handleEditTime = (timeBlockId, activityIndex) => {
    setEditingTime({ timeBlockId, activityIndex });
  };

  const handleSaveTime = (timeBlockId, activityIndex, newTime) => {
    const updatedActivities = [...localTimeBlocks[timeBlockId]];
    updatedActivities[activityIndex] = {
      ...updatedActivities[activityIndex],
      time: newTime
    };
    
    setLocalTimeBlocks(prev => ({
      ...prev,
      [timeBlockId]: updatedActivities
    }));
    
    setEditingTime(null);
    setHasChanges(true);
  };

  const handleCancelTimeEdit = () => {
    setEditingTime(null);
  };

  const confirmRemoveActivity = () => {
    if (removingActivity) {
      setLocalTimeBlocks(prev => ({
        ...prev,
        [removingActivity.timeBlockId]: prev[removingActivity.timeBlockId].filter((_, index) => index !== removingActivity.activityIndex)
      }));
      setHasChanges(true);
      setRemovingActivity(null);
    }
  };

  const cancelRemoveActivity = () => {
    setRemovingActivity(null);
  };

  if (!isOpen) return null;

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-start bg-black/50 backdrop-blur-sm pt-16 pb-4 px-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full my-auto max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex flex-shrink-0 justify-between items-start p-5 bg-white border-b border-gray-200">
          <div className="flex-1 min-w-0 pr-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Edit Day {dayIndex + 1} - {day.parks?.join(' & ') || 'Itinerary Day'}
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Drag and drop activities between time blocks to reorganize your day
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1.5 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-5">
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {timeBlocks.map((timeBlock) => {
                const Icon = timeBlock.icon;
                const activities = localTimeBlocks[timeBlock.id] || [];
                
                // Color classes (using explicit classes instead of dynamic Tailwind)
                const colorClasses = {
                  yellow: {
                    bg: 'bg-yellow-50',
                    border: 'border-yellow-200',
                    text: 'text-yellow-600',
                    borderDrag: 'border-yellow-400',
                    bgDrag: 'bg-yellow-50',
                    borderEmpty: 'border-yellow-200',
                    bgEmpty: 'bg-yellow-25'
                  },
                  orange: {
                    bg: 'bg-orange-50',
                    border: 'border-orange-200',
                    text: 'text-orange-600',
                    borderDrag: 'border-orange-400',
                    bgDrag: 'bg-orange-50',
                    borderEmpty: 'border-orange-200',
                    bgEmpty: 'bg-orange-25'
                  },
                  purple: {
                    bg: 'bg-purple-50',
                    border: 'border-purple-200',
                    text: 'text-purple-600',
                    borderDrag: 'border-purple-400',
                    bgDrag: 'bg-purple-50',
                    borderEmpty: 'border-purple-200',
                    bgEmpty: 'bg-purple-25'
                  }
                };
                
                const colors = colorClasses[timeBlock.color] || colorClasses.yellow;
                
                return (
                  <div key={timeBlock.id} className="space-y-3">
                    {/* Time Block Header */}
                    <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg ${colors.bg} ${colors.border} border`}>
                      <Icon className={`w-4 h-4 ${colors.text}`} />
                      <h3 className="text-sm font-semibold text-gray-900">{timeBlock.title}</h3>
                      <span className="px-2 py-0.5 ml-auto text-xs font-medium text-gray-600 bg-white rounded border border-gray-300">
                        {activities.length}
                      </span>
                    </div>

                    {/* Droppable Area */}
                    <Droppable droppableId={timeBlock.id}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`min-h-[200px] p-3 rounded-lg border-2 border-dashed transition-colors ${
                            snapshot.isDraggingOver
                              ? `${colors.borderDrag} ${colors.bgDrag}`
                              : `${colors.borderEmpty} bg-gray-50/30`
                          }`}
                        >
                          {activities.length === 0 ? (
                            <div className="flex justify-center items-center h-32 text-gray-400">
                              <div className="text-center">
                                <Icon className="mx-auto mb-1.5 w-6 h-6 opacity-40" />
                                <p className="text-xs font-medium">No activities</p>
                                <p className="text-xs opacity-75">Drag items here</p>
                              </div>
                            </div>
                          ) : (
                            activities.map((activity, index) => {
                              const ActivityIcon = getActivityIcon(activity.type);
                              return (
                                <Draggable
                                  key={`${activity.name}-${index}`}
                                  draggableId={`${activity.name}-${index}`}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`mb-2 p-2.5 rounded-lg border transition-all ${
                                        snapshot.isDragging
                                          ? 'shadow-lg scale-105 rotate-1 z-50'
                                          : 'shadow-sm hover:shadow-md'
                                      } ${getActivityColor(activity.type)}`}
                                    >
                                      <div className="flex gap-2.5 items-start">
                                        <div
                                          {...provided.dragHandleProps}
                                          className="flex-shrink-0 pt-0.5 p-0.5 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                                        >
                                          <GripVertical className="w-3.5 h-3.5" />
                                        </div>
                                        <ActivityIcon className="flex-shrink-0 w-3.5 h-3.5 mt-0.5" />
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm font-medium text-gray-900 truncate">
                                            {activity.name}
                                          </div>
                                          {activity.time && (
                                            <div className="flex items-center gap-1.5 mt-1">
                                              {editingTime?.timeBlockId === timeBlock.id && editingTime?.activityIndex === index ? (
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                  <input
                                                    type="time"
                                                    defaultValue={activity.time}
                                                    className="text-xs px-2 py-1 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    onKeyDown={(e) => {
                                                      if (e.key === 'Enter') {
                                                        handleSaveTime(timeBlock.id, index, e.target.value);
                                                      } else if (e.key === 'Escape') {
                                                        handleCancelTimeEdit();
                                                      }
                                                    }}
                                                    onBlur={(e) => handleSaveTime(timeBlock.id, index, e.target.value)}
                                                    autoFocus
                                                  />
                                                  <button
                                                    onClick={(e) => {
                                                      const input = e.target.previousElementSibling;
                                                      handleSaveTime(timeBlock.id, index, input?.value || activity.time);
                                                    }}
                                                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                                                  >
                                                    Save
                                                  </button>
                                                  <button
                                                    onClick={handleCancelTimeEdit}
                                                    className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                                                  >
                                                    Cancel
                                                  </button>
                                                </div>
                                              ) : (
                                                <div className="flex items-center gap-1.5">
                                                  <span className="text-xs text-gray-600">
                                                    {activity.time}
                                                  </span>
                                                  <button
                                                    onClick={() => handleEditTime(timeBlock.id, index)}
                                                    className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
                                                  >
                                                    Edit
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          {activity.location && (
                                            <div className="text-xs text-gray-500 mt-0.5 truncate">
                                              {activity.location}
                                            </div>
                                          )}
                                        </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveActivity(timeBlock.id, index);
                                          }}
                                          className="flex-shrink-0 p-1 text-gray-400 rounded-md transition-colors hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                                          title="Remove activity"
                                          aria-label="Remove activity"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>

            {/* Dining Section */}
            <div className="p-4 mt-6 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="flex gap-2 items-center mb-3 text-sm font-semibold text-gray-900">
                <Utensils className="w-4 h-4" />
                Dining Options
              </h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="block mb-1.5 text-xs font-medium text-gray-700">
                    Lunch
                  </label>
                  <input
                    type="text"
                    value={localDining.lunch?.name || ''}
                    onChange={(e) => setLocalDining(prev => ({
                      ...prev,
                      lunch: { ...prev.lunch, name: e.target.value }
                    }))}
                    className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter lunch option"
                  />
                </div>
                <div>
                  <label className="block mb-1.5 text-xs font-medium text-gray-700">
                    Dinner
                  </label>
                  <input
                    type="text"
                    value={localDining.dinner?.name || ''}
                    onChange={(e) => setLocalDining(prev => ({
                      ...prev,
                      dinner: { ...prev.dinner, name: e.target.value }
                    }))}
                    className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter dinner option"
                  />
                </div>
              </div>
            </div>
          </DragDropContext>
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 justify-between items-center px-5 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex gap-2 items-center">
            {hasChanges && (
              <span className="flex gap-1.5 items-center text-xs font-medium text-amber-600">
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse"></div>
                Unsaved changes
              </span>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={handleReset}
              className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white rounded-md border border-gray-300 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset
            </button>
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white rounded-md border border-gray-300 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex gap-1.5 items-center px-4 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Save className="w-3.5 h-3.5" />
              Save Changes
            </button>
          </div>
        </div>
      </div>

      {/* Remove Confirmation Dialog */}
      {removingActivity && (
        <div className="flex fixed inset-0 justify-center items-center bg-black bg-opacity-75 z-60">
          <div className="p-6 mx-4 w-full max-w-md bg-white rounded-xl shadow-2xl">
            <div className="flex gap-3 items-center mb-4">
              <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-full">
                <X className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Remove Activity</h3>
            </div>
            <p className="mb-6 text-gray-600">
              Are you sure you want to remove <strong>"{removingActivity.activity.name}"</strong> from the {removingActivity.timeBlockId} section?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelRemoveActivity}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveActivity}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700"
              >
                Remove Activity
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DragDropActivity;
